/**
 * AuthLayout — shared two-column shell used by /signup and /login.
 *
 * Left: narrative panel (logo, headline, animated diagram, value bullets,
 * trust row, terms footer). Hidden on screens below `lg`.
 *
 * Right: form panel that hosts the Clerk widget plus a custom header,
 * subhead, and "switch flow" link (e.g. "Already have an account?").
 *
 * IMPORTANT: this component is presentational only — no state, no effects.
 * Callers (Signup, Login) are also expected to remain pure-render to
 * preserve the load-bearing constraint that <SignUp> / <SignIn> never
 * remount unexpectedly. See `Signup.tsx` doc comment.
 */

import type { ReactNode } from 'react'
import { SpoofioDiagram } from './SpoofioDiagram'

type AuthLayoutProps = {
  /** Right-panel header above the Clerk widget (e.g. "Create your account"). */
  formTitle: string
  /** Right-panel subhead (e.g. trust line: "Free 14-day trial. No credit card required."). */
  formSubtitle: string
  /** The Clerk <SignUp /> or <SignIn /> element. */
  children: ReactNode
  /** Switch-flow link rendered below the form (e.g. "Already have an account? Sign in"). */
  switchPrompt: string
  switchLinkText: string
  switchLinkHref: string
  /** Optional terms footer rendered at the bottom of the right panel (signup only). */
  showTermsFooter?: boolean
}

function SpoofioMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 100 100" aria-hidden="true">
      <circle cx="50" cy="50" r="45" fill="currentColor" />
      <path
        d="M30 50 L45 65 L70 35"
        stroke="white"
        strokeWidth="8"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  )
}

const VALUE_BULLETS = [
  'Protected in under 60 seconds — paste one line of HTML',
  'Real-time alerts the moment a fake page loads',
  'Screenshot evidence and full forensic trail per detection',
] as const

export function AuthLayout({
  formTitle,
  formSubtitle,
  children,
  switchPrompt,
  switchLinkText,
  switchLinkHref,
  showTermsFooter = false,
}: AuthLayoutProps) {
  return (
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-2">
      {/* Left: brand narrative panel */}
      <aside
        className="hidden lg:flex lg:flex-col lg:justify-between bg-gradient-to-br from-primary-50 via-white to-primary-100 px-12 py-12"
        aria-label="Spoofio overview"
      >
        <div className="flex items-center gap-3 text-primary-700">
          <SpoofioMark className="h-9 w-9" />
          <span className="text-xl font-bold tracking-tight text-gray-900">
            Spoofio
          </span>
        </div>

        <div className="mx-auto max-w-md space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 leading-tight">
              Catch phishing pages the moment they go live.
            </h1>
            <p className="text-base text-gray-600 leading-relaxed">
              Spoofio plants an invisible marker on your pages. The instant a
              copy goes live on a fake site, you get an alert with the URL,
              location, and who visited it.
            </p>
          </div>

          <SpoofioDiagram compact />

          <ul className="space-y-3">
            {VALUE_BULLETS.map((bullet) => (
              <li key={bullet} className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary-600 text-white">
                  <CheckIcon className="h-3.5 w-3.5" />
                </span>
                <span className="text-sm text-gray-700">{bullet}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium uppercase tracking-wide text-gray-500">
          <span className="flex items-center gap-1.5">
            <CheckIcon className="h-3.5 w-3.5 text-primary-600" />
            No credit card
          </span>
          <span className="flex items-center gap-1.5">
            <CheckIcon className="h-3.5 w-3.5 text-primary-600" />
            60-second setup
          </span>
          <span className="flex items-center gap-1.5">
            <CheckIcon className="h-3.5 w-3.5 text-primary-600" />
            Cancel anytime
          </span>
        </div>
      </aside>

      {/* Right: form panel */}
      <main className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-16">
        <div className="mx-auto w-full max-w-md">
          {/* Mobile-only logo + tagline (left panel hidden on mobile) */}
          <div className="lg:hidden mb-8 text-center">
            <div className="inline-flex items-center gap-2 text-primary-700">
              <SpoofioMark className="h-10 w-10" />
              <span className="text-2xl font-bold tracking-tight text-gray-900">
                Spoofio
              </span>
            </div>
            <p className="mt-3 text-sm text-gray-600">
              Catch phishing pages the moment they go live.
            </p>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900">
              {formTitle}
            </h2>
            <p className="mt-1.5 text-sm text-gray-600">{formSubtitle}</p>
          </div>

          <div className="w-full">{children}</div>

          <p className="mt-6 text-center text-sm text-gray-600">
            {switchPrompt}{' '}
            <a
              href={switchLinkHref}
              className="font-semibold text-primary-600 hover:text-primary-700"
            >
              {switchLinkText}
            </a>
          </p>

          {showTermsFooter && (
            <p className="mt-6 text-center text-xs text-gray-500">
              By creating an account you agree to our{' '}
              <a
                href="/terms"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="/privacy"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                Privacy Policy
              </a>
              .
            </p>
          )}
        </div>
      </main>
    </div>
  )
}
